import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon, 
  UserIcon,
  ShieldCheckIcon,
  TrashIcon,
  CogIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import ModulePermissionMatrix from './ModulePermissionMatrix'
import UserDetailModal from './UserDetailModal'

const UserManagement = () => {
  const { 
    getAllUsers, 
    approveUser, 
    rejectUser, 
    deleteUser, 
    isAdmin
  } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [processingUsers, setProcessingUsers] = useState(new Set())
  const [apiError, setApiError] = useState(null)
  // Removed API testing state
  const [retryCount, setRetryCount] = useState(0)
  // Removed deployment status tracking
  
  // Module permission management state
  const [activeView, setActiveView] = useState('users') // 'users' or 'permissions'
  const [selectedUser, setSelectedUser] = useState(null)
  const [showUserModal, setShowUserModal] = useState(false)

  // Fetch users on component mount.
  useEffect(() => {
    fetchUsers()
  }, [])

  // Removed API testing initialization effect

  const fetchUsers = async (isRetry = false) => {
    console.log('🔍 UserManagement: fetchUsers called, setting loading to true')
    setLoading(true)
    setApiError(null)
    // Update retry count for UI feedback
    if (isRetry) {
      setRetryCount(prev => prev + 1)
    } else {
      setRetryCount(0)
    }
    
    try {
      console.log('🔍 UserManagement: Calling getAllUsers...')
      const { data, error } = await getAllUsers()
      console.log('🔍 UserManagement: getAllUsers returned:', { data, error, dataLength: data?.length })
      
      if (error) {
        console.log('🔍 UserManagement: API Error detected')
        
        // API-focused error handling
        let errorMessage = 'Failed to fetch users'
        let isApiDeploymentIssue = false
        let isAuthenticationIssue = false
        
        if (error.message?.includes('404') || error.message?.includes('Not Found')) {
          errorMessage = 'Admin API endpoints not found - likely deployment issue'
          isApiDeploymentIssue = true
        } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          errorMessage = 'Authentication failed - token may be expired'
          isAuthenticationIssue = true
        } else if (error.message?.includes('403') || error.message?.includes('Forbidden')) {
          errorMessage = 'Access denied - admin privileges required'
          isAuthenticationIssue = true
        } else if (error.message?.includes('500')) {
          errorMessage = 'Server error in admin API - check Supabase Edge Functions logs'
        }
        
        setApiError({
          message: errorMessage,
          isApiDeploymentIssue,
          isAuthenticationIssue,
          originalError: error.message,
          timestamp: new Date().toISOString(),
          retryCount: retryCount + (isRetry ? 1 : 0)
        })
        
        toast.error(errorMessage)
        console.error('API Error fetching users:', error)
      } else {
        console.log('🔍 UserManagement: Success path, setting users to:', data || [])
        setUsers(data || [])
        setApiError(null)
        setRetryCount(0)
        
        // Show success message if this was a retry
        if (isRetry && retryCount > 0) {
          toast.success('Connection restored - users loaded successfully')
        }
      }
    } catch (error) {
      console.log('🔍 UserManagement: Catch block')
      
      // Enhanced exception handling
      let errorMessage = 'An unexpected error occurred'
      let isConnectionIssue = false
      
      if (error.message?.includes('timeout')) {
        errorMessage = 'Request timed out - the server is not responding'
        isConnectionIssue = true
      } else if (error.message?.includes('fetch') || error.message?.includes('network')) {
        errorMessage = 'Network error - please check your connection'
        isConnectionIssue = true
      }
      
      setApiError({
        message: errorMessage,
        isConnectionIssue,
        timestamp: new Date().toISOString(),
        retryCount: retryCount + (isRetry ? 1 : 0)
      })
      
      toast.error(errorMessage)
      console.error('Error:', error)
    } finally {
      console.log('🔍 UserManagement: Finally block, setting loading to false')
      setLoading(false)
    }
  }

  const handleApproveUser = async (userId) => {
    if (processingUsers.has(userId)) return

    setProcessingUsers(prev => new Set([...prev, userId]))
    
    try {
      console.log('🔍 UserManagement: Attempting to approve user:', userId)
      const { error } = await approveUser(userId)
      
      if (error) {
        // Enhanced error handling
        let errorMessage = 'Failed to approve user'
        if (error.message?.includes('timeout')) {
          errorMessage = 'Database timeout - please try again'
        } else if (error.message?.includes('connection')) {
          errorMessage = 'Database connection error - please try again'
        } else if (error.message?.includes('network')) {
          errorMessage = 'Network error - please try again'
        }
        
        toast.error(errorMessage)
        console.error('🔍 UserManagement: Error approving user:', error)
      } else {
        toast.success('User approved successfully')
        console.log('🔍 UserManagement: User approved successfully')
        // Refresh the users list
        fetchUsers()
      }
    } catch (error) {
      // Enhanced exception handling
      let errorMessage = 'An unexpected error occurred'
      if (error.message?.includes('timeout')) {
        errorMessage = 'Request timed out - please try again'
      } else if (error.message?.includes('fetch')) {
        errorMessage = 'Network error - please check your connection'
      }
      
      toast.error(errorMessage)
      console.error('🔍 UserManagement: Exception approving user:', error)
    } finally {
      setProcessingUsers(prev => {
        const newSet = new Set(prev)
        newSet.delete(userId)
        return newSet
      })
    }
  }

  const handleRejectUser = async (userId) => {
    if (processingUsers.has(userId)) return

    if (!window.confirm('Are you sure you want to reject this user?')) {
      return
    }

    setProcessingUsers(prev => new Set([...prev, userId]))
    
    try {
      console.log('🔍 UserManagement: Attempting to reject user:', userId)
      const { error } = await rejectUser(userId)
      
      if (error) {
        // Enhanced error handling
        let errorMessage = 'Failed to reject user'
        if (error.message?.includes('timeout')) {
          errorMessage = 'Database timeout - please try again'
        } else if (error.message?.includes('connection')) {
          errorMessage = 'Database connection error - please try again'
        } else if (error.message?.includes('network')) {
          errorMessage = 'Network error - please try again'
        }
        
        toast.error(errorMessage)
        console.error('🔍 UserManagement: Error rejecting user:', error)
      } else {
        toast.success('User rejected')
        console.log('🔍 UserManagement: User rejected successfully')
        // Refresh the users list
        fetchUsers()
      }
    } catch (error) {
      // Enhanced exception handling
      let errorMessage = 'An unexpected error occurred'
      if (error.message?.includes('timeout')) {
        errorMessage = 'Request timed out - please try again'
      } else if (error.message?.includes('fetch')) {
        errorMessage = 'Network error - please check your connection'
      }
      
      toast.error(errorMessage)
      console.error('🔍 UserManagement: Exception rejecting user:', error)
    } finally {
      setProcessingUsers(prev => {
        const newSet = new Set(prev)
        newSet.delete(userId)
        return newSet
      })
    }
  }

  const handleDeleteUser = async (userId, userEmail) => {
    if (processingUsers.has(userId)) return

    // Enhanced confirmation for break-glass admin
    if (userEmail === 'stephen@seckar.com.au') {
      toast.error('Cannot delete break-glass admin account')
      return
    }

    if (!window.confirm(`⚠️ PERMANENT DELETION WARNING ⚠️

Are you sure you want to permanently delete user "${userEmail}"?

This action will:
• Remove the user from the authentication system
• Delete their profile and application data
• Revoke all access immediately
• Cannot be undone

Type "DELETE" in the next dialog to confirm this irreversible action.`)) {
      return
    }

    // Second confirmation requiring typing DELETE
    const confirmText = window.prompt(`To confirm deletion of "${userEmail}", please type: DELETE`)
    if (confirmText !== 'DELETE') {
      toast.error('Deletion cancelled - confirmation text did not match')
      return
    }

    setProcessingUsers(prev => new Set([...prev, userId]))
    
    try {
      console.log('🔍 UserManagement: Attempting to delete user:', userEmail)
      const { data, error } = await deleteUser(userId)
      
      if (error) {
        // Enhanced error handling with specific messages for different error types
        console.error('🔍 UserManagement: Error deleting user:', error)
        
        if (error.critical_inconsistency) {
          // Critical error: profile deleted but auth deletion failed
          toast.error('CRITICAL: Database inconsistency detected!', { 
            duration: 15000,
            style: {
              background: '#FEE2E2',
              color: '#991B1B',
              border: '2px solid #DC2626',
              fontWeight: 'bold'
            }
          })
          toast.error(`Profile was deleted but auth deletion failed. Manual cleanup required for user ${userId}`, { 
            duration: 12000 
          })
          console.error('🔍 UserManagement: CRITICAL INCONSISTENCY:', error)
        } else if (error.cleanup_required) {
          // Error requiring manual cleanup
          toast.error('Deletion partially failed - manual cleanup needed', { duration: 10000 })
          toast.error(error.message, { duration: 8000 })
          console.error('🔍 UserManagement: Cleanup required:', error)
        } else {
          // Standard error handling
          const errorMessage = error.message || 'Failed to delete user'
          toast.error(errorMessage, { duration: 6000 })
        }

        // Log detailed error information for debugging
        console.error('🔍 UserManagement: Detailed error info:', {
          error: error,
          userId: userId,
          userEmail: userEmail,
          timestamp: new Date().toISOString()
        })
      } else {
        // Handle success with enhanced feedback based on deletion completeness
        console.log('🔍 UserManagement: Deletion response:', data)
        
        if (data?.complete === true) {
          // Complete deletion (both auth and profile successfully deleted)
          toast.success(data.message || 'User deleted successfully from both auth and profiles', {
            duration: 5000,
            style: {
              background: '#F0FDF4',
              color: '#166534',
              border: '1px solid #16A34A'
            }
          })
          console.log('🔍 UserManagement: Complete user deletion successful:', data)
        } else {
          // Partial deletion or user not found in profiles
          if (data?.warning) {
            // Show main success message first
            toast.success(data.message || 'User deleted from authentication system', {
              duration: 4000
            })
            
            // Then show warning about profile deletion
            setTimeout(() => {
              toast.error(`Warning: ${data.warning}`, { 
                duration: 8000,
                style: {
                  background: '#FEF3C7',
                  color: '#92400E',
                  border: '1px solid #F59E0B'
                }
              })
            }, 1000)
            
            console.warn('🔍 UserManagement: Partial deletion warning:', data.warning)
          } else {
            // Standard success
            toast.success(data.message || 'User deleted successfully', { duration: 4000 })
          }
          
          if (data?.requiresAdminAction) {
            // Additional notice for admin action
            setTimeout(() => {
              toast('⚠️ Note: Additional cleanup may be needed', {
                duration: 6000,
                style: {
                  background: '#EFF6FF',
                  color: '#1E40AF',
                  border: '1px solid #3B82F6'
                }
              })
            }, 2500)
          }
          
          console.log('🔍 UserManagement: Partial user deletion completed:', data)
        }
        
        // Refresh the users list to reflect changes
        await fetchUsers()
      }
    } catch (error) {
      toast.error('An unexpected error occurred during user deletion')
      console.error('🔍 UserManagement: Exception during user deletion:', error)
    } finally {
      setProcessingUsers(prev => {
        const newSet = new Set(prev)
        newSet.delete(userId)
        return newSet
      })
    }
  }

  // Removed legacy diagnostics/test utilities
  const handleRunDiagnostics = () => {}

  // Test individual API endpoint (removed/no-op)
  const handleTestSingleEndpoint = async (endpointKey) => {
    // Block any health endpoint tests
    if (endpointKey && (endpointKey.includes('health') || endpointKey.includes('test-health'))) {
      console.error('🔧 UserManagement: Health endpoint testing blocked - endpoint removed')
      toast.error('Health endpoint no longer available', { id: 'single-test' })
      return
    }
    
    try {
      toast('Diagnostics disabled in production UI', { id: 'single-test' })

    } catch (error) {
      console.error(`🔍 UserManagement: ${endpointKey} test error:`, error)
      toast.error('Diagnostics disabled', { id: 'single-test' })
    }
  }

  // Removed bulk API test function

  // User selection handlers
  const handleUserSelect = (user) => {
    setSelectedUser(user)
    setShowUserModal(true)
  }

  const handleCloseUserModal = () => {
    setShowUserModal(false)
    setSelectedUser(null)
  }

  const handleRefreshData = () => {
    fetchUsers()
  }

  const getStatusBadge = (status, isAdmin) => {
    if (isAdmin) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <ShieldCheckIcon className="w-3 h-3 mr-1" />
          Admin
        </span>
      )
    }

    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Approved
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-3 h-3 mr-1" />
            Pending
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="w-3 h-3 mr-1" />
            Rejected
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Unknown
          </span>
        )
    }
  }

  const pendingUsers = users.filter(user => user.status === 'pending')
  const approvedUsers = users.filter(user => user.status === 'approved')
  const rejectedUsers = users.filter(user => user.status === 'rejected')

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">
          Access denied. Admin privileges required.
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage user access and module permissions for the E8 Tracker application
            </p>
          </div>
          
          {/* View Toggle */}
          <div className="flex rounded-lg bg-gray-100 p-1">
            <button
              onClick={() => setActiveView('users')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
                activeView === 'users'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <UserIcon className="w-4 h-4 mr-2" />
              Users
            </button>
            <button
              onClick={() => setActiveView('permissions')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
                activeView === 'permissions'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <TableCellsIcon className="w-4 h-4 mr-2" />
              Permissions Matrix
            </button>
          </div>
        </div>
      </div>


      {/* API Error Banner */}
      {apiError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Admin API Error Detected
                </h3>
                <div className="mt-1 text-sm text-red-700">
                  <span>{apiError.message}</span>
                  <br />
                  <span className="text-xs">
                    Error: {apiError.originalError} | 
                    Retry Count: {apiError.retryCount} | 
                    Time: {new Date(apiError.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action buttons in banner */}
            <div className="flex gap-2">
              <button
                onClick={() => fetchUsers(true)}
                className="inline-flex items-center px-2 py-1 border border-blue-300 shadow-sm text-xs leading-4 font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <ClockIcon className="h-8 w-8 text-yellow-500" />
            <div className="ml-3">
              <p className="text-2xl font-semibold text-gray-900">{pendingUsers.length}</p>
              <p className="text-sm text-gray-600">Pending Approval</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
            <div className="ml-3">
              <p className="text-2xl font-semibold text-gray-900">{approvedUsers.length}</p>
              <p className="text-sm text-gray-600">Approved Users</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-8 w-8 text-red-500" />
            <div className="ml-3">
              <p className="text-2xl font-semibold text-gray-900">{rejectedUsers.length}</p>
              <p className="text-sm text-gray-600">Rejected</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <UserIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-2xl font-semibold text-gray-900">{users.length}</p>
              <p className="text-sm text-gray-600">Total Users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Conditional View Rendering */}
      {activeView === 'permissions' ? (
        <ModulePermissionMatrix onUserSelect={handleUserSelect} />
      ) : (
        <>
          {/* Users Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">All Users</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => fetchUsers(false)}
                disabled={loading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          {/* API test results removed */}

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-600">Loading users...</p>
              <p className="mt-1 text-xs text-gray-500">This should complete within 10 seconds</p>
            </div>
          ) : apiError ? (
            <div className="text-center py-8">
              <XCircleIcon className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-red-900">Connection Error</h3>
              <p className="mt-1 text-sm text-red-600">{apiError?.message}</p>
              
              {/* Enhanced error details */}
            <div className="mt-2 text-xs text-gray-500 space-y-1">
                <p>Error occurred at: {new Date().toLocaleTimeString()}</p>
                {retryCount > 0 && (
                  <p>Retry attempts: {retryCount}</p>
                )}
              {apiError?.isApiDeploymentIssue && (
                <p className="text-yellow-600">API endpoints may still be deploying</p>
              )}
              </div>

              {/* Conditional retry options based on error type */}
              <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
                <button
                  onClick={() => fetchUsers(true)}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {loading ? 'Retrying...' : `Try Again${retryCount > 0 ? ` (${retryCount + 1})` : ''}`}
                </button>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
              <p className="mt-1 text-sm text-gray-500">No users have registered yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                              <UserIcon className="h-4 w-4 text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(user.status, user.is_admin)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2 items-center">
                          {user.status === 'pending' && !user.is_admin && (
                            <>
                              <button
                                onClick={() => handleApproveUser(user.id)}
                                disabled={processingUsers.has(user.id)}
                                className="text-green-600 hover:text-green-900 disabled:opacity-50"
                              >
                                {processingUsers.has(user.id) ? 'Processing...' : 'Approve'}
                              </button>
                              <button
                                onClick={() => handleRejectUser(user.id)}
                                disabled={processingUsers.has(user.id)}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              >
                                Reject
                              </button>
                              <button
                                onClick={() => handleUserSelect(user)}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                title="Manage permissions"
                              >
                                <CogIcon className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {user.status === 'approved' && !user.is_admin && (
                            <>
                              <span className="text-gray-500">Approved</span>
                              <button
                                onClick={() => handleUserSelect(user)}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                title="Manage permissions"
                              >
                                <CogIcon className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {user.status === 'rejected' && !user.is_admin && (
                            <>
                              <button
                                onClick={() => handleApproveUser(user.id)}
                                disabled={processingUsers.has(user.id)}
                                className="text-green-600 hover:text-green-900 disabled:opacity-50"
                              >
                                {processingUsers.has(user.id) ? 'Processing...' : 'Re-approve'}
                              </button>
                              <button
                                onClick={() => handleUserSelect(user)}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                title="Manage permissions"
                              >
                                <CogIcon className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {user.is_admin && (
                            <span className="text-purple-600">Admin</span>
                          )}
                          {!user.is_admin && (
                            <button
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              disabled={processingUsers.has(user.id)}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50 p-1"
                              title={`Delete user permanently (ID: ${user.id})`}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
          </>
        )}

      {/* User Detail Modal */}
      <UserDetailModal
        user={selectedUser}
        isOpen={showUserModal}
        onClose={handleCloseUserModal}
        onRefresh={handleRefreshData}
      />
    </div>
  )
}

export default UserManagement
